import * as React from 'react';
import { Theme, alpha } from '@mui/material';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useToastError } from '@hooks';
import { Spot, useSupdateSpotAtmosphereMutation, UpdateSpotAtmosphereInput } from '@graphql';
import { TriStateCheckbox } from '../common';

interface SpotAtmosphereProps {
  account: Spot;
}

export function SpotAtmosphere(props: SpotAtmosphereProps) {
  const { account } = props;
  const [timeoutId, setTimeoutId] = React.useState<NodeJS.Timeout | null>(null);
  const toastError = useToastError();
  const { t } = useTranslation('cta');

  const [updateSpotAtmosphere] = useSupdateSpotAtmosphereMutation();

  const initialValues = React.useMemo(() => {
    return Object.entries(account.atmosphere)
      .filter(([key, value]) => !['__typename', 'id', 'updatedAt'].includes(key))
      .reduce((acc, [key, value]) => {
        acc[key as keyof UpdateSpotAtmosphereInput] = value;
        return acc;
      }, {} as Record<keyof UpdateSpotAtmosphereInput, boolean | null>);
  }, [account.atmosphere]);

  const validationSchema = React.useMemo(() => {
    return yup.object(
      Object.keys(account.atmosphere)
        .filter((key) => !['__typename', 'id', 'updatedAt'].includes(key))
        .reduce((acc, key) => {
          acc[key as keyof UpdateSpotAtmosphereInput] = yup.boolean().nullable().defined();
          return acc;
        }, {} as Record<keyof UpdateSpotAtmosphereInput, yup.BooleanSchema<boolean | null>>)
    );
  }, [account.atmosphere]);

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      if (formik.dirty) {
        updateSpotAtmosphere({
          variables: { id: account.id, input: values },
          onCompleted: ({ supdateSpotAtmosphere }) => {
            setTimeoutId(null);
            formik.resetForm({ values });
            toast(t('spotAtmosphere.toast.success', { ns: 'field' }), { type: 'success' });
          },
          onError: toastError,
        });
      }
    },
  });

  React.useEffect(() => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }

    if (formik.dirty && formik.isValid) {
      const newTmoutId = setTimeout(formik.submitForm, 1000);
      setTimeoutId(newTmoutId);
    }
  }, [formik.dirty, formik.isValid, formik.values]);

  if (account?.__typename !== 'Spot') {
    return null;
  }

  return (
    <Stack width="100%" spacing={3}>
      <Stack
        direction="row"
        p={3}
        justifyContent="space-between"
        sx={{
          borderRadius: (theme: Theme) => theme.borderRadius,
          backgroundColor: alpha('#fff', 0.1),
        }}
      >
        <Stack>
          <Typography fontWeight="bold">{t('spotAtmosphere.label', { ns: 'field' })}</Typography>
          <Typography variant="caption" color="text.secondary">
            {t('spotAtmosphere.caption', { ns: 'field' })}
          </Typography>
        </Stack>
      </Stack>
      <Stack
        spacing={2}
        px={2}
        divider={<Divider sx={{ backgroundColor: (theme: Theme) => theme.palette.grey[800] }} />}
      >
        {Object.keys(account.atmosphere)
          .filter((key) => !['__typename', 'id', 'updatedAt'].includes(key))
          .map((key) => (
            <TriStateCheckbox
              key={key}
              label={t(`atmosphere.${key}`, { ns: 'spot' })}
              dataKey={key}
              formik={formik}
            />
          ))}
      </Stack>
    </Stack>
  );
}